import React, { FunctionComponent, ReactElement, useCallback } from "react";

import { Icon, IconButton, Tooltip, Typography, styled } from "@mui/material";

import { InfoOutlined } from "@mui/icons-material";

import copy from "copy-to-clipboard";
import { format } from "date-fns";
import { useParams } from "react-router-dom";

import { Paper } from "../../components/common";
import {
    useGetBulkOrderById,
    useGetProductVariantByIds,
    useMessage,
} from "../../sdk/hooks";
import { IMessageContext, IProductImage } from "../../sdk/types";
import { formatCurrency } from "../../sdk/utils/numbers";
import { reverseCountryMapping } from "../../utils/constants";

const Root = styled("main")`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(2)};

    padding: ${({ theme }) => theme.spacing(3)};
    margin: ${({ theme }) => theme.spacing(0, "auto")};

    ${({ theme }) => `
        ${theme.breakpoints.down("sm")} {
            padding: ${theme.spacing(2)};
    }
  `}
`;

const Title = styled(Typography)`
    padding: ${({ theme }) => theme.spacing(0, 3)};

    font-size: ${({ theme }) => theme.spacing(3)};
    font-weight: 600;
    text-transform: capitalize;

    ${({ theme }) => `
    ${theme.breakpoints.down("sm")} {
      padding: 0;
    }
  `}
`;

const SectionTitle = styled(Typography)`
    font-size: ${({ theme }) => theme.spacing(3)};
    font-weight: 500;
    color: black;

    ${({ theme }) => `
        ${theme.breakpoints.down("lg")} {
            font-size: ${theme.spacing(2)};
    }
  `}

    margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const SummaryHeading = styled("div")`
    font-size: 28px;
    font-weight: 600;
    color: #111111;
    letter-spacing: -0.4px;

    ${({ theme }) => `
    ${theme.breakpoints.down("sm")} {
      font-size: 22px;
    }
  `}
`;

const ReceivedLine = styled("div")`
    font-size: ${({ theme }) => theme.spacing(2)};
    font-weight: 400;
    color: #6a6a6a;

    margin-top: ${({ theme }) => theme.spacing(0.5)};
    margin-bottom: ${({ theme }) => theme.spacing(2.5)};
`;

const ReceivedDate = styled("span")`
    font-weight: 600;
    color: #202020;
`;

/*
 * Quiet section label above the requested products, so the summary line above
 * it stays the loudest thing in the panel.
 */
const SectionLabel = styled("div")`
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: #8e8e8e;

    margin-bottom: ${({ theme }) => theme.spacing(2)};
`;
const ItemContainer = styled("div")`
    display: flex;
    flex-direction: column;

    margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

const ItemValueRow = styled("div")`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(0.5)};

    min-width: 0;
`;

const ItemLink = styled("a")`
    font-size: ${({ theme }) => theme.spacing(2)};
    font-weight: 500;
    color: ${({ theme }) => theme.palette.primary.main};

    /*
     * Sitting the rule clear of the descenders keeps a long address legible
     * while still reading as a link.
     */
    text-decoration: underline;
    text-underline-offset: 3px;
    text-decoration-thickness: 1px;

    overflow-wrap: anywhere;

    &:hover {
        text-decoration-thickness: 2px;
    }
`;

const ItemDataContainer = styled("div")`
    font-size: ${({ theme }) => theme.spacing(2)};
    font-weight: 500;
    color: black;
`;

const ItemDataReasonContainer = styled("div")`
    width: 100%;
    min-height: 120px;

    border-radius: 4px;

    font-size: ${({ theme }) => theme.spacing(2)};
    font-weight: 500;

    color: #8e8e8e;
    background-color: #efefef;

    padding: ${({ theme }) => theme.spacing(1)};
`;

const ItemDataHeader = styled("div")`
    font-size: ${({ theme }) => theme.spacing(2)};
    font-weight: 400;
    color: #6a6a6a;
`;

const LeftContainerr = styled("section")`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(3)};
    flex: 1;

    width: 60%;

    ${({ theme }) => `
    ${theme.breakpoints.down("md")} {
      max-width: unset;
      width: 100%;
    }
    
  `}
`;

const RightContainer = styled("section")`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(3)};

    width: 40%;

    ${({ theme }) => `
    ${theme.breakpoints.down("md")} {
          padding-top: 0px;
          width: 100%;
    }
    
  `}
`;

const TopContainer = styled("div")`
    padding: ${({ theme }) => theme.spacing(0, 3, 3, 3)};

    display: flex;
    gap: ${({ theme }) => theme.spacing(2)};

    ${({ theme }) => `
    ${theme.breakpoints.down("md")} {
      flex-direction:column;
      gap: ${theme.spacing(3)};
    }
    ${theme.breakpoints.down("sm")} {
      padding: ${theme.spacing(0, 0, 2, 0)};
    }
  `}
`;

const Product = styled("div")`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(3)};

    width: 100%;
`;

const ProductBlock = styled("div")`
    display: flex;
    flex-direction: column;
`;

const ProductRow = styled("div")`
    display: flex;
    gap: ${({ theme }) => theme.spacing(2)};
    align-items: center;

    min-width: 0;

    margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

/*
 * Label and value on one line, separated by a rule, so quantity and the two
 * prices read as a list rather than a grid that has to be aligned by hand.
 */
const DetailRow = styled("div")`
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: ${({ theme }) => theme.spacing(2)};

    padding: ${({ theme }) => theme.spacing(1.5, 0)};
    border-top: 1px solid #ececec;
`;

const DetailLabel = styled("div")`
    font-size: ${({ theme }) => theme.spacing(2)};
    font-weight: 400;
    color: #6a6a6a;
`;

const DetailValue = styled("div")`
    font-size: ${({ theme }) => theme.spacing(2)};
    font-weight: 600;
    color: #202020;

    text-align: right;
    overflow-wrap: anywhere;
`;

const UnspecifiedValue = styled(DetailValue)`
    font-weight: 400;
    color: #8e8e8e;
`;

/*
 * The warm tint and the icon carry the note on their own, and the body drops
 * to 13px so it reads as an aside rather than another paragraph of detail.
 */
const NoteBox = styled("div")`
    display: flex;
    align-items: flex-start;
    gap: ${({ theme }) => theme.spacing(1.25)};

    background-color: #fff9e9;
    border: 1px solid #f2e2b6;
    border-radius: 6px;

    padding: ${({ theme }) => theme.spacing(1.25, 1.5)};
    margin-top: ${({ theme }) => theme.spacing(2.5)};
`;

const NoteIcon = styled(InfoOutlined)`
    flex: 0 0 auto;

    font-size: 18px;
    color: #c98a00;

    margin-top: 1px;
`;

const NoteContent = styled("div")`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(0.25)};

    min-width: 0;
`;

const NoteTitle = styled("div")`
    font-size: 13px;
    font-weight: 600;
    color: #7a5800;
`;

const NoteText = styled("div")`
    font-size: 13px;
    font-weight: 400;
    color: #5f5f5f;
    line-height: 1.4;
`;

const ProductImage = styled("img")`
    height: 88px;
    width: 88px;

    flex: 0 0 auto;

    object-fit: cover;
    border-radius: ${({ theme }) => theme.spacing(1)};
`;

const ProductTitleGroup = styled("div")`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(0.25)};

    min-width: 0;
`;

const ProductTitle = styled("div")`
    font-size: ${({ theme }) => theme.spacing(2.5)};
    font-weight: 600;
    color: #202020;

    overflow-wrap: anywhere;
`;

const VariantTitle = styled("div")`
    font-size: ${({ theme }) => theme.spacing(2)};
    font-weight: 400;
    color: #6a6a6a;

    overflow-wrap: anywhere;
`;

const SkuText = styled("div")`
    font-size: 13px;
    font-weight: 400;
    color: #9a9a9a;

    overflow-wrap: anywhere;
`;
const EmptyMessage = styled("div")`
    font-size: ${({ theme }) => theme.spacing(2)};
    font-weight: 400;
    color: #6a6a6a;

    padding: ${({ theme }) => theme.spacing(2, 0)};
`;

/*
 * A variant's first image is as likely to be a spec shot or an infographic as
 * the product itself, so prefer the image tagged for listing display, then
 * PLP, then the PDP main image — the order the storefront and the cart
 * thumbnails already use.
 */
const displayImageTags = ["listing", "plp", "main-desktop", "main-mobile"];

const hasTag = (image: IProductImage, tag: string): boolean =>
    (image.tags ?? "")
        .split(",")
        .some((value) => value.trim().toLowerCase() === tag);

const selectDisplayImage = (
    images: IProductImage[] = [],
): IProductImage | undefined => {
    for (const tag of displayImageTags) {
        const tagged = images.find((image) => hasTag(image, tag));

        if (tagged) {
            return tagged;
        }
    }

    return images[0];
};

interface IRequesterDetail {
    header: string;
    data: string;
    /* Present only on rows worth copying or linking out to. */
    copyValue?: string;
    href?: string;
}

const copyText = (text: string, message: IMessageContext) => {
    if (copy(text, { format: "text/plain" })) {
        message.showSuccess("Copied to clipboard");
    } else {
        message.showError("Failed to copy to clipboard");
    }
};

const formatPhoneNumber = (phoneNumber: string) => {
    return phoneNumber.replace(/^(\+\d{2})(\d+)/, "$1 $2");
};

const pluralise = (word: string, count: number): string =>
    count === 1 ? word : `${word}s`;

/*
 * Variant prices come off the products endpoint in paise, unconverted. The
 * bulk order response is already in rupees, so only the variant needs this.
 */
const paiseToRupees = (paise: number): number => paise / 100;

const formatUnits = (quantity?: number): string =>
    typeof quantity === "number"
        ? `${quantity} ${pluralise("unit", quantity)}`
        : "NA";

const formatDate = (value?: string | Date | null): string =>
    value ? format(new Date(value), "d MMM yyyy") : "NA";

export const BulkOrderScreen: FunctionComponent = (): ReactElement => {
    const params = useParams();

    const bulkOrderQuery = useGetBulkOrderById(
        "bulk-detail",
        params.bulkOrderId ?? "",
    );

    const productVariants = useGetProductVariantByIds({
        variantIds:
            bulkOrderQuery?.data?.bulkOrderLineItems.map(
                (item) => item.variantId,
            ) ?? [],
        key: "variants",
    });

    const message = useMessage();

    const handleCopy = useCallback(
        (value: string) => () => copyText(value, message),
        [message],
    );

    const bulkOrder = bulkOrderQuery.data?.bulkOrder;
    const lineItems = bulkOrderQuery.data?.bulkOrderLineItems ?? [];

    const unitCount = lineItems.reduce(
        (total, lineItem) => total + (lineItem.quantity ?? 0),
        0,
    );

    const requestSummary = `${lineItems.length} ${pluralise(
        "product",
        lineItems.length,
    )} · ${unitCount} ${pluralise("unit", unitCount)}`;

    /*
     * Asking price is optional on the form, so an empty one means the buyer
     * wants us to open the negotiation rather than that anything is missing.
     */
    const hasOpenAskingPrice = lineItems.some(
        (lineItem) => !lineItem.askingPrice,
    );

    const bulkOrderData: IRequesterDetail[] = [
        {
            header: "Name",
            data: bulkOrder
                ? [bulkOrder.firstName, bulkOrder.lastName]
                      .filter(Boolean)
                      .join(" ")
                : "NA",
        },
        {
            header: "Phone Number",
            data: bulkOrder?.phoneNumber
                ? formatPhoneNumber(bulkOrder.phoneNumber)
                : "NA",
            /*
             * Copy the unformatted number: it is what a dialer or WhatsApp
             * expects, while the spaced form is only easier to read.
             */
            copyValue: bulkOrder?.phoneNumber,
            href: bulkOrder?.phoneNumber
                ? `tel:${bulkOrder.phoneNumber}`
                : undefined,
        },
        {
            header: "Email",
            data: bulkOrder?.emailAddress || "NA",
            copyValue: bulkOrder?.emailAddress,
            href: bulkOrder?.emailAddress
                ? `mailto:${bulkOrder.emailAddress}`
                : undefined,
        },
        {
            /*
             * Requests carry a country name rather than a code, so fall back to
             * the stored value when the mapping has no entry for it.
             */
            header: "Country",
            data:
                (bulkOrder?.country &&
                    reverseCountryMapping[bulkOrder.country]) ||
                bulkOrder?.country ||
                "NA",
        },
        {
            header: "State",
            data: bulkOrder?.state || "NA",
        },
        {
            header: "Total Asking Price",
            data: `₹${bulkOrder?.totalAskingPrice ?? 0}`,
        },
        {
            header: "Total Selling Price",
            data: `₹${bulkOrder?.totalSellingPrice ?? 0}`,
        },
        {
            header: "Scheduled Call Date",
            data: formatDate(bulkOrder?.scheduledCallDate),
        },
        {
            header: "Scheduled Call Start Time",
            data: bulkOrder?.scheduledCallStartTime || "NA",
        },
        {
            header: "Scheduled Call End Time",
            data: bulkOrder?.scheduledCallEndTime || "NA",
        },
    ];

    return (
        <Root>
            <Title>Bulk Order Request</Title>
            <TopContainer>
                <LeftContainerr>
                    <Paper>
                        <SummaryHeading>{requestSummary}</SummaryHeading>
                        <ReceivedLine>
                            Received{" "}
                            <ReceivedDate>
                                {formatDate(bulkOrder?.createdAt)}
                            </ReceivedDate>
                        </ReceivedLine>

                        <SectionLabel>
                            {lineItems.length === 1
                                ? "Product requested"
                                : "Products requested"}
                        </SectionLabel>

                        <Product>
                            {bulkOrderQuery.isSuccess &&
                                lineItems.length === 0 && (
                                    <EmptyMessage>
                                        No products were specified with this
                                        request.
                                    </EmptyMessage>
                                )}
                            {bulkOrderQuery.isSuccess &&
                                lineItems.length > 0 &&
                                productVariants.variants.length === 0 && (
                                    <EmptyMessage>
                                        Product details are unavailable for this
                                        request.
                                    </EmptyMessage>
                                )}
                            {productVariants.variants.length > 0 &&
                                lineItems.length > 0 &&
                                productVariants.variants.map((item, index) => {
                                    const lineItem = lineItems[index];
                                    const displayImage = selectDisplayImage(
                                        item.images,
                                    );
                                    const askingPrice = lineItem?.askingPrice;

                                    return (
                                        <ProductBlock
                                            key={lineItem?.id ?? index}
                                        >
                                            <ProductRow>
                                                {displayImage?.objectUrl && (
                                                    <ProductImage
                                                        src={
                                                            displayImage.objectUrl
                                                        }
                                                        alt={
                                                            displayImage.altText ??
                                                            item.product.title
                                                        }
                                                    />
                                                )}
                                                <ProductTitleGroup>
                                                    <ProductTitle>
                                                        {item.product.title}
                                                    </ProductTitle>
                                                    {item.title && (
                                                        <VariantTitle>
                                                            {item.title}
                                                        </VariantTitle>
                                                    )}
                                                    {item.sku && (
                                                        <SkuText>
                                                            {item.sku}
                                                        </SkuText>
                                                    )}
                                                </ProductTitleGroup>
                                            </ProductRow>

                                            <DetailRow>
                                                <DetailLabel>
                                                    Quantity
                                                </DetailLabel>
                                                <DetailValue>
                                                    {formatUnits(
                                                        lineItem?.quantity,
                                                    )}
                                                </DetailValue>
                                            </DetailRow>

                                            <DetailRow>
                                                <DetailLabel>
                                                    Your list price
                                                </DetailLabel>
                                                {typeof item.price ===
                                                "number" ? (
                                                    <DetailValue>
                                                        {formatCurrency(
                                                            paiseToRupees(
                                                                item.price,
                                                            ),
                                                        )}
                                                    </DetailValue>
                                                ) : (
                                                    <UnspecifiedValue>
                                                        Not available
                                                    </UnspecifiedValue>
                                                )}
                                            </DetailRow>

                                            <DetailRow>
                                                <DetailLabel>
                                                    Buyer&apos;s asking price
                                                </DetailLabel>
                                                {askingPrice ? (
                                                    <DetailValue>
                                                        {formatCurrency(
                                                            askingPrice,
                                                        )}
                                                    </DetailValue>
                                                ) : (
                                                    <UnspecifiedValue>
                                                        Not specified
                                                    </UnspecifiedValue>
                                                )}
                                            </DetailRow>
                                        </ProductBlock>
                                    );
                                })}

                            {hasOpenAskingPrice && (
                                <NoteBox>
                                    <NoteIcon />
                                    <NoteContent>
                                        <NoteTitle>Price left open</NoteTitle>
                                        <NoteText>
                                            No asking price was suggested. Use
                                            the contact details to follow up
                                            with a quote.
                                        </NoteText>
                                    </NoteContent>
                                </NoteBox>
                            )}
                        </Product>
                    </Paper>
                </LeftContainerr>
                <RightContainer>
                    <Paper>
                        <SectionTitle>Requester Details</SectionTitle>
                        {bulkOrderData.map((detail) => {
                            const { copyValue, href } = detail;

                            return (
                                <ItemContainer key={detail.header}>
                                    <ItemDataHeader>
                                        {detail.header}
                                    </ItemDataHeader>
                                    <ItemValueRow>
                                        {href ? (
                                            <ItemLink href={href}>
                                                {detail.data}
                                            </ItemLink>
                                        ) : (
                                            <ItemDataContainer>
                                                {detail.data}
                                            </ItemDataContainer>
                                        )}
                                        {copyValue && (
                                            <Tooltip title="Copy">
                                                <IconButton
                                                    size="small"
                                                    aria-label={`Copy ${detail.header.toLowerCase()}`}
                                                    onClick={handleCopy(
                                                        copyValue,
                                                    )}
                                                >
                                                    <Icon fontSize="small">
                                                        content_copy
                                                    </Icon>
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </ItemValueRow>
                                </ItemContainer>
                            );
                        })}
                        <ItemContainer>
                            <ItemDataHeader>Reason</ItemDataHeader>
                            <ItemDataReasonContainer>
                                {bulkOrder?.reason || "No reason provided."}
                            </ItemDataReasonContainer>
                        </ItemContainer>
                    </Paper>
                </RightContainer>
            </TopContainer>
        </Root>
    );
};
